import React from 'react';
import {
  TableCellProps,
  TableCellAlignment,
  TableCellType,
} from '../../../types/DesignSystem/Table';
import { stickyBodyCellClasses } from './stickyColumn';
import { DENSITY_PY } from './TableHeader';
import { withClassName } from '../cn';

/**
 * `align` has to move the CONTENT, not just the text.
 *
 * It was `text-align` alone, which works for a text run and does nothing at all to a
 * block-level child. Measured on the real component in headless Chromium: the `Active`
 * column declared `align: 'center'`, the cell computed `text-align: center`, and the
 * `Toggle` inside it — root `display: flex`, therefore block-level — spanned the entire
 * content box, `gapLeft: 0`, `gapRight: 0`. Not centred by one pixel. The same would be
 * true of a `Dropdown`, a `TextField`, a button, or any other control placed in a cell,
 * which is most of what a `cellType` union naming `dropdown`, `textfield` and `toggle`
 * expects to hold.
 *
 * So alignment is expressed twice, on purpose:
 *
 * - `justify-*` on an inner flex row, which positions block-level and inline children
 *   alike. This is the half that was missing.
 * - `text-align` still on the `<td>`, because a single long text run becomes one
 *   anonymous flex item and its *wrapped* lines are aligned by `text-align`, not by
 *   `justify-content`.
 *
 * **Why not `display: flex` on the `<td>` itself** — which is what `cellType: 'icon'`
 * used to do: a flex `<td>` stops generating a table-cell box, so it drops out of the
 * table's column sizing and the row's alignment. That was a latent bug rather than an
 * observed one only because nothing in the app uses `cellType: 'icon'` yet.
 */
const alignStyles: Record<TableCellAlignment, string> = {
  left: 'tw-text-left',
  center: 'tw-text-center',
  right: 'tw-text-right',
};

const justifyStyles: Record<TableCellAlignment, string> = {
  left: 'tw-justify-start',
  center: 'tw-justify-center',
  right: 'tw-justify-end',
};

/**
 * Cell type base styles for styling hints.
 *
 * `icon` lost `tw-flex tw-items-center tw-justify-center` — see above; centering an icon
 * is what `align="center"` is for, and it now works.
 */
const cellTypeStyles: Record<TableCellType, string> = {
  text: '',
  numerical: 'tw-font-medium tw-tabular-nums',
  meta: 'tw-tabular-nums',
  icon: '',
  badge: '',
  dropdown: '',
  textfield: '',
  toggle: '',
  tag: '',
  custom: '',
};

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(function TableCell({
  align = 'left',
  cellType = 'text',
  density = 'default',
  className = '',
  children,
  sticky,
  'data-testid': dataTestId,
  ...rest
}, ref) {
  /* The ink is chosen, not appended: `meta` (§ 66) needs `ink-secondary`, and two
     ink classes on one cell would leave the winner to stylesheet order. */
  const inkStyle = cellType === 'meta' ? 'tw-text-ink-secondary' : 'tw-text-ink';
  /* § 245 — the vertical padding is the density tier's, shared with the header. */
  const baseStyles = `tw-px-3 ${DENSITY_PY[density]} tw-text-body-primary ${inkStyle}`;
  /* Position/offset/z/divider only. The opaque ground a pinned cell needs arrives
     via `className` from `TableRow`, which is the only place that knows whether this
     row is striped and whether the table is hoverable. */
  const stickyClass = stickyBodyCellClasses(sticky);

  return (
    <td
      {...rest}
      ref={ref}
      className={withClassName(`${baseStyles} ${alignStyles[align]} ${cellTypeStyles[cellType]} ${stickyClass}`, className)}
      data-testid={dataTestId}
    >
      {/* `items-center` rather than `items-stretch`: a chip and a line of text in the
          same row must share a centre line, and the cell's own `vertical-align: middle`
          only centres the cell box, not two children of differing height inside it. */}
      <div className={`tw-flex tw-items-center ${justifyStyles[align]}`}>{children}</div>
    </td>
  );
});

export default TableCell;
